import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  Assessment as AssessmentIcon,
  Route as RouteIcon,
  Rule as RuleIcon,
  Psychology as PsychologyIcon,
  Flag as FlagIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePatientFilter } from '../hooks/usePatientFilter'
import {
  journeyMilestonesAPI,
  assessmentResultsAPI,
  compositionAPI,
  ptmAPI,
  patientsAPI,
} from '../services/api'

const severityStyles = {
  none: { color: '#71717a', label: 'None' },
  minimal: { color: '#16a34a', label: 'Minimal' },
  mild: { color: '#0284c7', label: 'Mild' },
  moderate: { color: '#ca8a04', label: 'Moderate' },
  moderately_severe: { color: '#ea580c', label: 'Mod. Severe' },
  severe: { color: '#dc2626', label: 'Severe' },
}

function SeverityChip({ severity }) {
  if (!severity) return <Typography variant="body2" color="text.secondary">—</Typography>
  const style = severityStyles[severity] || { color: '#71717a', label: severity.replace('_', ' ') }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: style.color }} />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function SectionHeader({ icon: Icon, title, count, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {Icon && <Icon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
        {title}
      </Typography>
      {typeof count === 'number' && (
        <Chip
          label={count}
          size="small"
          sx={{ ml: 1, height: 18, fontSize: '0.6875rem' }}
        />
      )}
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
  )
}

function RuleLogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false)
  const isStrategy = entry.rule_type === 'strategy'
  const rules = isStrategy
    ? entry.execution_data?.strategies || []
    : entry.execution_data?.rules || []
  const matched = rules.filter(r => r.matched).length

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5 }}
        onClick={() => setExpanded(!expanded)}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: '1rem',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s',
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
          {new Date(entry.evaluated_at).toLocaleString()}
        </Typography>
        <Chip
          label={isStrategy ? 'Strategy' : 'Patient'}
          size="small"
          color={isStrategy ? 'primary' : 'secondary'}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.6875rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          {entry.trigger_event}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {matched}/{rules.length} matched
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 3, pt: 0.5, pb: 1 }}>
          {rules.map((rule, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: rule.matched ? '#16a34a' : '#d4d4d8',
                  mt: 0.6,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                  {rule.strategy_name || rule.rule_name || `Rule #${rule.strategy_id || rule.rule_id}`}
                </Typography>
                {rule.conditions?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Conditions: {rule.conditions.map(c =>
                      `${c.type} ${c.operator || ''} ${c.value ?? ''}`
                    ).join(', ')}
                  </Typography>
                )}
                {rule.matched && rule.actions_applied?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Actions: {rule.actions_applied.map(a =>
                      `${a.type}${a.value ? `(${a.value})` : ''}${a.risk_code ? `(${a.risk_code})` : ''}`
                    ).join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

function PatientDashboard() {
  const navigate = useNavigate()
  const { selectedPatient, hasSelectedPatient } = usePatientFilter()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [milestones, setMilestones] = useState([])
  const [assessments, setAssessments] = useState([])
  const [context, setContext] = useState(null)
  const [trackStates, setTrackStates] = useState([])
  const [ruleLog, setRuleLog] = useState([])
  const [ptm, setPtm] = useState(null)
  const [enrollment, setEnrollment] = useState(null)

  const fetchAll = useCallback(async (patientId) => {
    setLoading(true)
    setError(null)
    try {
      const results = await Promise.allSettled([
        journeyMilestonesAPI.getMilestonesByUserId(patientId),
        assessmentResultsAPI.getAssessmentResultsByUserId(patientId),
        compositionAPI.getContext(patientId),
        compositionAPI.getTrackStates(patientId),
        compositionAPI.getRuleLog(patientId),
        ptmAPI.get(patientId).catch(() => null),
        patientsAPI.getCurrentEnrollment(patientId).catch(() => null),
      ])

      // Journey milestones route wraps in { result: [...] }
      const milestonesData = results[0].status === 'fulfilled' ? results[0].value.data : null
      setMilestones(Array.isArray(milestonesData) ? milestonesData : (milestonesData?.result || []))

      // Assessment results returns a plain array
      const assessmentsData = results[1].status === 'fulfilled' ? results[1].value.data : null
      setAssessments(Array.isArray(assessmentsData) ? assessmentsData : [])

      // The composition/context endpoint wraps the payload as
      // { success: true, context: {...} } — unwrap to the inner object so
      // `context?.risk_factors` etc. resolve correctly.
      setContext(results[2].status === 'fulfilled' ? results[2].value.data?.context : null)
      // getTrackStates + getRuleLog already auto-unwrap to arrays in api.js
      setTrackStates(results[3].status === 'fulfilled' && Array.isArray(results[3].value) ? results[3].value : [])
      setRuleLog(results[4].status === 'fulfilled' && Array.isArray(results[4].value) ? results[4].value : [])
      setPtm(results[5].status === 'fulfilled' ? results[5].value : null)
      // getCurrentEnrollment returns the enrollment object directly (or null/404)
      setEnrollment(results[6].status === 'fulfilled' ? results[6].value : null)
    } catch (err) {
      console.error('Error loading patient dashboard:', err)
      setError('Failed to load patient dashboard')
    } finally {
      setLoading(false)
    }
  }, [])

  const patientId = selectedPatient?.id
  useEffect(() => {
    if (patientId) {
      fetchAll(patientId)
    } else {
      setMilestones([])
      setAssessments([])
      setContext(null)
      setTrackStates([])
      setRuleLog([])
      setPtm(null)
      setEnrollment(null)
    }
  }, [patientId, fetchAll])

  if (!hasSelectedPatient()) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Patient Dashboard</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a patient from the dropdown above to view their complete dashboard.
        </Alert>
      </Box>
    )
  }

  const latestAssessmentsByCode = Object.values(
    (assessments || []).reduce((acc, a) => {
      if (!acc[a.instrument_code] || new Date(a.submitted_date) > new Date(acc[a.instrument_code].submitted_date)) {
        acc[a.instrument_code] = a
      }
      return acc
    }, {})
  )

  // The composition/context API already returns only active risk factors
  // (UserRiskFactors.findActiveByUserId filters by is_active = true AND
  // not expired). The previous .filter(rf => rf.active) silently dropped
  // everything because that field doesn't exist in the response shape.
  const activeRiskFactors = context?.risk_factors || []

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5">Patient Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedPatient.name || selectedPatient.email || 'Unnamed'}
          {selectedPatient.age ? ` · Age ${selectedPatient.age}` : ''}
          {selectedPatient.sex ? ` · ${selectedPatient.sex}` : ''}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* Enrollment & Program */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader
                icon={FlagIcon}
                title="Current Intervention"
                action={enrollment && (
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate('/patient-assignments')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Manage
                  </Button>
                )}
              />
              {enrollment ? (
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {enrollment.intervention_name || `Program #${enrollment.intervention_config_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Status: {enrollment.status || enrollment.enrollment_status || 'active'}
                  </Typography>
                  {(enrollment.enrolled_at || enrollment.enrollment_date) && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      Enrolled: {new Date(enrollment.enrolled_at || enrollment.enrollment_date).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enrolled in any intervention.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Active Risk Factors */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <SectionHeader
                icon={FavoriteIcon}
                title="Active Risk Factors"
                count={activeRiskFactors.length}
              />
              {activeRiskFactors.length === 0 ? (
                <Typography variant="body2" color="text.secondary">None active.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {activeRiskFactors.map((rf, i) => (
                    <Chip
                      key={i}
                      label={`${rf.risk_code}${rf.value != null && rf.value !== '' ? ` · ${rf.value}` : ''}`}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Health Data */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader
                icon={PersonIcon}
                title="Health Data"
                count={milestones.length}
                action={
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate('/journey-milestones')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Manage
                  </Button>
                }
              />
              {milestones.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No health data entries.</Typography>
              ) : (
                <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                  {milestones.slice(0, 10).map((m) => (
                    <Box key={m.id} sx={{ display: 'flex', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Chip
                        label={m.type}
                        size="small"
                        sx={{ height: 18, fontSize: '0.6875rem', textTransform: 'capitalize', mt: 0.25 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {m.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(m.date).toLocaleDateString()}
                          {m.treatment_description ? ` · ${m.treatment_description}` : ''}
                          {m.diagnosis_description ? ` · ${m.diagnosis_description}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Latest Assessments */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader
                icon={AssessmentIcon}
                title="Latest Assessments"
                count={latestAssessmentsByCode.length}
                action={
                  <Button
                    size="small"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate('/assessment-results')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Manage
                  </Button>
                }
              />
              {latestAssessmentsByCode.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No assessment results.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem' }}>Instrument</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>Score</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>Severity</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {latestAssessmentsByCode.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            {a.instrument_code}
                            {a.instrument_name ? ` — ${a.instrument_name}` : ''}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{a.calculated_score ?? '—'}</TableCell>
                          <TableCell><SeverityChip severity={a.severity} /></TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            {a.submitted_date ? new Date(a.submitted_date).toLocaleDateString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Pathway / Track States */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader icon={RouteIcon} title="Pathway State" count={trackStates.length} />
              {trackStates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active pathways for this patient.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  {trackStates.map((t) => (
                    <Box
                      key={t.track_id || t.id}
                      sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                          {t.track_name || t.track_code || `Track #${t.track_id || t.id}`}
                        </Typography>
                        {t.track_type && (
                          <Chip
                            label={t.track_type}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.6875rem' }}
                          />
                        )}
                        {t.status && (
                          <Chip
                            label={t.status}
                            size="small"
                            color={t.status === 'active' ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.6875rem', ml: 'auto' }}
                          />
                        )}
                      </Box>
                      {t.context && Object.keys(t.context).length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                          {Object.entries(t.context).slice(0, 3).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Rule Execution Log */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader icon={RuleIcon} title="Recent Rule Executions" count={ruleLog.length} />
              {ruleLog.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No rule executions recorded.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  {ruleLog.slice(0, 15).map((entry, idx) => (
                    <RuleLogEntry key={entry.id || idx} entry={entry} />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Therapeutic Model (condensed) */}
          {ptm && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <SectionHeader
                  icon={PsychologyIcon}
                  title="Therapeutic Model"
                  action={
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      onClick={() => navigate('/therapeutic-models')}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      View Full
                    </Button>
                  }
                />
                <Grid container spacing={2}>
                  {ptm.session_strategy && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Session Strategy
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                        {ptm.session_strategy}
                      </Typography>
                    </Grid>
                  )}
                  {ptm.treatment_arc_summary && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Treatment Arc
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                        {ptm.treatment_arc_summary}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      v{ptm.version || 0} · {ptm.session_count || 0} sessions · {(ptm.issue_register || []).length} issues · {(ptm.therapeutic_hypotheses || []).length} hypotheses
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  )
}

export default PatientDashboard
